import "./_CategoryPanel.scss";
import "./_CategoryPanelMobile.scss";
import { useEffect, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";

function _CategoryPanel() {
  const [categoryName, setCategoryName] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [checked, setChecked] = useState([]);
  const [isEditing, setIsEditing] = useState(false); // ✅ edit mode
  const [editingId, setEditingId] = useState(null); // ✅ category being edited

  // 🟢 Fetch categories
  const fetchCategories = () => {
    fetch(`${process.env.REACT_APP_BASE_URL}api/product/get-category`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setCategoryData(data.categories);
        setChecked(new Array(data.categories.length).fill(false));
      })
      .catch((err) => console.error("Error fetching categories:", err));
  };

  useEffect(() => {
    fetchCategories();
  }, [setIsEditing]);

  // 🟡 Save or Update category
  const handleSave = () => {
    if (!categoryName.trim()) {
      alert("Category name cannot be empty");
      return;
    }

    if (isEditing) {
      // 🟢 Update existing category
      fetch(
        `https://littlekidinside.shop/api/product/update-category/${editingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ categoryName }),
        },
      )
        .then((res) => res.json())
        .then(() => {
          alert("Category updated successfully!");
          setCategoryName("");
          setIsEditing(false);
          setEditingId(null);
          fetchCategories();
        })
        .catch((err) => console.error("Error updating category:", err));
    } else {
      // 🟢 Add new category
      fetch(`https://littlekidinside.shop/api/product/add-category`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categoryName }),
      })
        .then((res) => res.json())
        .then(() => {
          alert("Category saved successfully!");
          setCategoryName("");
          fetchCategories();
        })
        .catch((err) => console.error("Error saving category:", err));
    }
  };
  // 🟠 Delete single category
  const handleDelete = (item) => {
    if (item.item_count > 0) {
      alert(
        `❌ Cannot delete "${item.PC_Name}" because it still has ${item.item_count} item(s).`,
      );
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${item.PC_Name}"?`)) {
      return;
    }

    fetch(
      `https://littlekidinside.shop/api/product/delete-category/${item.PC_ID}`,
      {
        method: "DELETE",
      },
    )
      .then((res) => res.json())
      .then((data) => {
        alert(`✅ "${item.PC_Name}" deleted successfully!`);
        fetchCategories(); // refresh after delete
      })
      .catch((err) => console.error("Error deleting category:", err));
  };

  // 🟠 Delete multiple selected categories
  const handleDeleteSelected = () => {
    const selectedItems = categoryData.filter((_, index) => checked[index]);
    const deletableItems = selectedItems.filter(
      (item) => item.item_count === 0,
    );
    const nonDeletableItems = selectedItems.filter(
      (item) => item.item_count > 0,
    );

    if (selectedItems.length === 0) {
      alert("Please select at least one category to delete.");
      return;
    }

    if (nonDeletableItems.length > 0) {
      const blockedNames = nonDeletableItems.map((i) => i.PC_Name).join(", ");
      alert(
        `❌ The following categories cannot be deleted because they still have items:\n${blockedNames}`,
      );
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${deletableItems.length} selected categories?`,
      )
    ) {
      return;
    }

    Promise.all(
      deletableItems.map((item) =>
        fetch(
          `${process.env.REACT_APP_BASE_URL}api/product/delete-category/${item.PC_ID}`,
          {
            method: "DELETE",
          },
        ),
      ),
    )
      .then(() => {
        alert(
          `✅ Successfully deleted ${deletableItems.length} category(ies)!`,
        );
        fetchCategories();
      })
      .catch((err) =>
        console.error("Error deleting selected categories:", err),
      );
  };

  // 🟠 Edit category
  const handleEdit = (item) => {
    setCategoryName(item.PC_Name);
    setIsEditing(true);
    setEditingId(item.PC_ID); // assuming backend returns PC_ID
  };

  // 🔵 Cancel editing
  const handleCancel = () => {
    setCategoryName("");
    setIsEditing(false);
    setEditingId(null);
  };

  // 🟠 Handle individual checkbox toggle
  const handleCheck = (index) => {
    const updatedChecked = [...checked];
    updatedChecked[index] = !updatedChecked[index];
    setChecked(updatedChecked);
  };

  // 🔵 Handle "Check All"
  const handleCheckAll = (e) => {
    const isChecked = e.target.checked;
    setChecked(new Array(categoryData.length).fill(isChecked));
  };

  const allChecked = checked.length > 0 && checked.every(Boolean);
  // 🔹 Add this new state near the top
  const [searchTerm, setSearchTerm] = useState("");

  // 🟢 Filtered category list (real-time)
  const filteredCategories = categoryData.filter((item) =>
    item.PC_Name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <main className="ad-category-container">
      <div className="cat-panel-header">
        <div className="cat-panel-title-wrapper">
          <span>CATEGORY PANEL</span>
        </div>
      </div>

      <div className="category-panel-container">
        <div className="category-list">
          {" "}
          <div className="actions">
            {checked.some((v) => v) && (
              <button
                className="delete-selected-btn"
                onClick={handleDeleteSelected}
              >
                DELETE
              </button>
            )}
            <input
              type="text"
              placeholder="Keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="cat-panel-table-wrapper">
            {" "}
            <table>
              <thead>
                <tr>
                  <th>NO.</th>
                  <th>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={handleCheckAll}
                    />
                  </th>
                  <th>Category Name</th>
                  <th>Items</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((item, index) => (
                    <tr
                      key={item.PC_ID}
                      className={editingId === item.PC_ID ? "editing-row" : ""}
                    >
                      <td>{index + 1}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={checked[index] || false}
                          onChange={() => handleCheck(index)}
                        />
                      </td>
                      <td>{item.PC_Name}</td>
                      <td>{item.item_count}</td>
                      <td className="action">
                        <div className="td-action-btns-wrapper">
                          <FaEdit
                            className="action-icon edit"
                            onClick={() => handleEdit(item)}
                          />
                          <FaTrash
                            className="action-icon delete"
                            onClick={() => handleDelete(item)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center" }}>
                      No categories found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="add-category">
          <div className="ac-input-wrapper">
            <label>{isEditing ? "Edit Category:" : "Add Category:"}</label>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
            />
          </div>
          <div className="ac-btn-action-wrapper">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="save-btn" onClick={handleSave}>
              {isEditing ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

export default _CategoryPanel;
